import React, { ForwardRefRenderFunction } from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { closeDialog } from "store/actions/utilActions";
import { useDispatch } from "react-redux";
import { invoiceCreated, invoiceCredited, invoiceSent, invoiceUpdated } from "store/actions/invoiceActions";
//#endregion
//#region types
import { StyleEnums } from "models/enums";
import { InvoicePreviewProps } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FormHandles } from "models/propTypes/genericFormProps";
import { DialogContentHandles } from "models/propTypes/dialogPropTypes";
//#endregion
//#region components
import {
  PageFooter,
  SimpleEntityTitle,
  ViewPanel,
  FormValidity,
  WidgetTitle,
  useFloatingMessages,
} from "components/common";
import InvoiceDetails from "./details";
import InvoicePreviewFormContainer from "./forms/previewFormContainer";
import { EntityPreviewLayout } from "components/common/pageLayouts";
import { useInvoiceService } from "serviceHooks";
//#endregion
import { InvoiceHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const InvoicePreview: ForwardRefRenderFunction<DialogContentHandles, InvoicePreviewProps> = (props, ref) => {
  const invoiceService = useInvoiceService();
  const { invoiceId, invoicePut: invoice, reloadInvoice, status, customerName, isReminder, version } = props;
  const formRef = React.useRef<FormHandles>(null);
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();

  const [pendingReloadId, setPendingReloadId] = React.useState<number>();
  const [reminderFees, setReminderFees] = React.useState<number>(0);
  const [errorOnSubmit, setErrorOnSubmit] = React.useState<boolean>(false);
  const [sendMethod, setSendMethod] = React.useState<WintSuperkollAPIModel.InvoiceSendMethod>();
  const [nextInvoiceSerialNumber, setNextInvoiceSerialNumber] = React.useState<string | undefined>();

  const { enqueueMessage } = useFloatingMessages();

  React.useEffect(() => {
    if (props.serialNumber) {
      setNextInvoiceSerialNumber(InvoiceHelper.formatInvoiceNumber(props.serialNumber));
      return;
    }

    invoiceService.getInvioceNextSerialNumber().then((nextSerialNumber) => {
      setNextInvoiceSerialNumber(InvoiceHelper.formatInvoiceNumber(nextSerialNumber));
    });
  }, []);

  // "invoice" is the snapshot captured when the preview opened, so a reference person added or changed
  // from the "Skicka som" box lives only in the form values. Without this the invoice would go out with
  // the old reference (or none at all) while the missing-reference-code warning had already cleared.
  const getCustomerReference = (values?: InvoiceFormTypes.Preview) =>
    values?.referencePersonId
      ? { referenceId: values.referencePersonId, referenceName: values.referencePersonName }
      : invoice?.customerReference;

  const getMailOptions = (values: InvoiceFormTypes.Preview) => {
    return {
      from: {
        type: values.sendMail?.emailFromType ?? WintSuperkollAPIModel.SendInvoiceMailsFromEnum.PersonMail,
        address: values.sendMail?.emailFrom,
      },
      to: {
        type: InvoiceHelper.getEmailToType(values.mailOptions?.defaultEmailTo, values.sendMail?.emailTo),
        address: values.sendMail?.emailTo
          ?.map((to) => to?.value)
          .filter((email) => email)
          .join(","),
      },
      subject: InvoiceHelper.revertSerialNumberToTemplate(values.sendMail?.emailSubject, nextInvoiceSerialNumber),
      message: values.sendMail?.emailMessage,
      version: version,
    };
  };

  React.useImperativeHandle(ref, () => ({
    onClose: onClosePreview,
  }));

  const onSaveAsDraftClick = async () => {
    const values: InvoiceFormTypes.Preview = formRef.current?.getValues && formRef.current?.getValues();
    if (invoice) {
      let id = invoiceId ? invoiceId : pendingReloadId;
      try {
        if (errorOnSubmit) {
          setErrorOnSubmit(false);
        }

        id = await invoiceService.createOrUpdateInvoice(
          id,
          {
            ...invoice,
            customerReference: getCustomerReference(values),
            notes: values ? values.notes : invoice.notes,
            attachments: (values
              ? values.attachments
              : invoice.attachments) as WintSuperkollAPIModel.InvoiceAttachment[],
            invoiceMailInfo:
              values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.Email
                ? {
                    invoiceSendMethod: WintSuperkollAPIModel.InvoiceSendMethod.Email,
                    mailOptions: getMailOptions(values),
                  }
                : {
                    invoiceSendMethod: values.sendType,
                  },
          },
          true
        );

        if (id) {
          setPendingReloadId(id);
        }
      } catch (error) {
        setErrorOnSubmit(true);
        setPendingReloadId(id);

        throw error;
      }
    }
  };

  const onSendClick = async (values: InvoiceFormTypes.Preview) => {
    let id = invoiceId ? invoiceId : pendingReloadId;
    try {
      if (errorOnSubmit) {
        setErrorOnSubmit(false);
      }

      if (status === WintSuperkollAPIModel.InvoiceStatus.NotSent && invoice) {
        id = await invoiceService.createOrUpdateInvoice(id, {
          ...invoice,
          customerReference: getCustomerReference(values),
          notes: values.notes,
          attachments: values.attachments as WintSuperkollAPIModel.InvoiceAttachment[],
          invoiceMailInfo: {
            invoiceSendMethod: values.sendType,
          },
        });
      } else if (isReminder && id) {
        await invoiceService.reminderPayment(id, {
          reminderDueDate: values.reminderDate,
          reminderCost: Number(values.reminderCost),
          reminderMessage: values.notes,
        });
      }

      if (id) {
        switch (values.sendType) {
          case WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent:
            if (status === WintSuperkollAPIModel.InvoiceStatus.NotSent) await invoiceService.markAsSent(id);
            await invoiceService.downloadPdf(id, version);
            break;
          case WintSuperkollAPIModel.InvoiceSendMethod.Print:
            if (status === WintSuperkollAPIModel.InvoiceStatus.NotSent) await invoiceService.sendAsPrint(id);
            else await invoiceService.printInvoice(id);
            break;
          case WintSuperkollAPIModel.InvoiceSendMethod.Email:
            const values: InvoiceFormTypes.Preview = formRef.current?.getValues && formRef.current?.getValues();
            const options: WintSuperkollAPIModel.InvoiceMailOptions = getMailOptions(values);
            if (status === WintSuperkollAPIModel.InvoiceStatus.NotSent) await invoiceService.sendAsEmail(id, options);
            else await invoiceService.emailInvoice(id, options);
            break;
        }
      }
    } catch (error) {
      setErrorOnSubmit(true);
      setPendingReloadId(id);

      throw error;
    }

    if (id) {
      closePreview(id, true);

      enqueueMessage(
        LocalizationHelperInstance.getFormattedMessage(
          values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.MarkAsSent
            ? TranslationKeys.Invoice.MarkedAsSentSuccessMessage
            : values.sendType === WintSuperkollAPIModel.InvoiceSendMethod.Print
              ? TranslationKeys.Invoice.PrintSuccessMessage
              : TranslationKeys.Invoice.SendMailSuccessMessage
        ),
        StyleEnums.DisplayType.Success
      );
    }
  };

  const onSubmit = (submitFunc: (values: any) => Promise<any>) => {
    return (formRef.current && formRef.current.submit(submitFunc)) || new Promise<void>((resolve) => resolve());
  };

  const onClosePreview = (id?: number, markAsSent?: boolean) => {
    const toReloadId = id ?? pendingReloadId;

    if (toReloadId) {
      if (reloadInvoice && ((invoiceId === undefined && pendingReloadId !== undefined) || !errorOnSubmit)) {
        reloadInvoice(toReloadId);
      }

      if (markAsSent) {
        dispatch(invoiceSent(toReloadId!));
        if (props.invoicePut?.creditedInvoiceId !== undefined) {
          dispatch(invoiceCredited(props.invoicePut?.creditedInvoiceId));
        }
      } else {
        invoiceService.get(toReloadId).then((invoice) => {
          if (invoiceId) {
            dispatch(invoiceUpdated(invoice));
          } else {
            dispatch(invoiceCreated(invoice));
          }
        });
      }
    }

    if (errorOnSubmit) {
      setErrorOnSubmit(false);
    }
  };

  const closePreview = (id?: number, markAsSent?: boolean) => {
    onClosePreview(id, markAsSent);
    dispatch(closeDialog());
  };

  const onReminderFeesUpdated = (fees: number) => {
    setReminderFees(fees);
  };

  const onInvoiceSendMethodSelected = (sendType?: WintSuperkollAPIModel.InvoiceSendMethod) => {
    setSendMethod(sendType);
  };

  return (
    <FormValidity
      render={(isValid, onValidityChange) => (
        <EntityPreviewLayout
          className={cssExports.invoicePreviewContainer}
          upperLeftComponent={
            <SimpleEntityTitle
              title={`${LocalizationHelperInstance.getFormattedMessage(
                TranslationKeys.Invoice.InvoiceTo2
              )} ${customerName}`}
              status={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ToReview)}
            />
          }
          footer={
            <PageFooter
              showNavigateBack
              actions={(status === WintSuperkollAPIModel.InvoiceStatus.NotSent
                ? [
                    {
                      text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SaveDraft),
                      onClick: onSaveAsDraftClick,
                      type: StyleEnums.DisplayType.Info,
                      isDisabled: false,
                      successMessage: TranslationKeys.Invoice.SaveSuccessMessage,
                      testId: "saveDraftButton",
                    },
                  ]
                : []
              ).concat([
                {
                  text: invoiceService.getInvoiceSendMethodText(sendMethod),
                  onClick: onSubmit.bind(null, onSendClick),
                  type: StyleEnums.DisplayType.Success,
                  isDisabled: !isValid,
                  successMessage: "",
                  testId: "sendButton",
                },
              ])}
              onBackButtonClick={closePreview.bind(null, undefined, undefined)}
            />
          }
        >
          <ViewPanel>
            <WidgetTitle>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ReviewMessage)}
            </WidgetTitle>
            <InvoiceDetails
              total={Number(props.total) + Number(reminderFees)}
              tax={props.tax}
              currency={props.currency}
              referencePersonName={props.referencePersonName}
              paymentTerms={props.paymentTerms}
              invoiceRows={props.invoiceRows}
            />
            <InvoicePreviewFormContainer
              ref={formRef}
              {...props}
              reminderFeesUpdateHandler={onReminderFeesUpdated}
              onInvoiceSendMethodSelected={onInvoiceSendMethodSelected}
              nextInvoiceSerialNumber={nextInvoiceSerialNumber}
            />
          </ViewPanel>
        </EntityPreviewLayout>
      )}
    />
  );
};

export default React.forwardRef(InvoicePreview);
